import { Heading, Link, LuIcon, SegmentedTabs, Text } from "@overmap-ai/blocks"
import { type Asset, type AssetType, type AssetTypeStatus, type Stored, EMPTY_ARRAY } from "@overmap-ai/common"
import { selectAssetsOfProject, selectAssetTypesOfOrganization, selectAssetTypeStatuses } from "@overmap-ai/core"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { memo, useCallback, useMemo } from "react"

import { AssetTypeCard } from "../../components/Cards/AssetTypeCard"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { IMPORT_ASSET_CSV_TEMPLATE } from "../../constants"
import { cardLayoutCva, interactiveCardCva } from "../../cva"
import { modelIconMapping } from "../../mappings/icons"
import { useAppSelector, useShallowEqaulSelector } from "../../store"
import { getFileDownloadAttributes } from "../../utils/file"

enum AssetTypesViewTabs {
	PROJECT = "project",
	OTHER = "other",
}

// TODO: should we just use Papaparse here to create the CSV template?
const downloadAttr = getFileDownloadAttributes("template.imports", IMPORT_ASSET_CSV_TEMPLATE)

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/asset-types/")

export const AssetTypesView = memo(() => {
	const { organizationId, projectId } = routeApi.useParams()

	const assetTypes = useShallowEqaulSelector(selectAssetTypesOfOrganization(organizationId))
	const assets = useShallowEqaulSelector(selectAssetsOfProject(projectId))
	const assetTypeStatuses = useAppSelector(selectAssetTypeStatuses)
	const navigate = useNavigate()

	const [projectAssetTypes, otherAssetTypes] = useMemo(() => {
		const assetTypeIdsWithAssets = new Set(assets.map((asset) => asset.asset_type))

		const projectAssetTypes: Stored<AssetType>[] = []
		const otherAssetTypes: Stored<AssetType>[] = []

		for (const assetType of assetTypes) {
			if (assetTypeIdsWithAssets.has(assetType.uuid)) {
				projectAssetTypes.push(assetType)
			} else {
				otherAssetTypes.push(assetType)
			}
		}

		return [projectAssetTypes, otherAssetTypes]
	}, [assetTypes, assets])

	const assetsByAssetType = useMemo(() => {
		const ret: Record<AssetType["uuid"], Stored<Asset>[]> = {}

		for (const asset of assets) {
			if (!(asset.asset_type in ret)) ret[asset.asset_type] = []
			ret[asset.asset_type].push(asset)
		}

		return ret
	}, [assets])

	const assetTypeStatusesByAssetType = useMemo(() => {
		const ret: Record<AssetType["uuid"], Stored<AssetTypeStatus>[]> = {}

		for (const issueTypeStatus of assetTypeStatuses) {
			if (!(issueTypeStatus.asset_type in ret)) ret[issueTypeStatus.asset_type] = []
			ret[issueTypeStatus.asset_type].push(issueTypeStatus)
		}

		return ret
	}, [assetTypeStatuses])

	const handleClickAssetTypeCard = useCallback(
		(assetType: Stored<AssetType>) => {
			void navigate({
				from: "/$organizationId/$projectId/asset-types/",
				to: "/$organizationId/$projectId/asset-types/$assetTypeId",
				params: (prev) => ({ ...prev, assetTypeId: assetType.uuid }),
			})
		},
		[navigate],
	)

	return (
		<ViewContainer maxWidth="6xl">
			<div className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex h-max w-full flex-col gap-1">
					<div className="flex items-end justify-between gap-2">
						<Heading size="xl">Assets</Heading>
					</div>

					<Text size="sm" accentColor="base">
						Assets allow you to track the state and location of assets in your project. Use{" "}
						<Link size="sm" {...downloadAttr}>
							this template
						</Link>{" "}
						to import new assets into a given type. Mass import is found in the more{" "}
						<LuIcon icon={modelIconMapping.more} style={{ display: "inline", verticalAlign: "bottom" }} />{" "}
						menu.
					</Text>
				</div>

				<SegmentedTabs.Root
					defaultValue={AssetTypesViewTabs.PROJECT}
					className="flex size-full flex-col gap-4 overflow-hidden"
				>
					<SegmentedTabs.List size="sm" accentColor="base" variant="surface">
						<SegmentedTabs.Trigger value={AssetTypesViewTabs.PROJECT}>
							Project {projectAssetTypes.length > 0 && `(${projectAssetTypes.length})`}
						</SegmentedTabs.Trigger>
						<SegmentedTabs.Trigger value={AssetTypesViewTabs.OTHER}>
							Other {otherAssetTypes.length > 0 && `(${otherAssetTypes.length})`}
						</SegmentedTabs.Trigger>
					</SegmentedTabs.List>

					<SegmentedTabs.Content
						value={AssetTypesViewTabs.PROJECT}
						className="size-full overflow-y-auto overflow-x-hidden"
					>
						<div className={cardLayoutCva()}>
							{projectAssetTypes.length > 0 ? (
								projectAssetTypes.map((assetType) => (
									<AssetTypeCard
										key={assetType.uuid}
										assetType={assetType}
										assets={assetsByAssetType[assetType.uuid] ?? EMPTY_ARRAY}
										assetTypeStatuses={assetTypeStatusesByAssetType[assetType.uuid] ?? EMPTY_ARRAY}
										onClick={handleClickAssetTypeCard}
										className={cx(interactiveCardCva(), "h-72 cursor-pointer")}
										size="sm"
									/>
								))
							) : (
								<Text accentColor="base" weight="regular" size="sm">
									No asset types.
								</Text>
							)}
						</div>
					</SegmentedTabs.Content>

					<SegmentedTabs.Content
						value={AssetTypesViewTabs.OTHER}
						className="size-full overflow-y-auto overflow-x-hidden"
					>
						<div className={cardLayoutCva()}>
							{otherAssetTypes.length > 0 ? (
								otherAssetTypes.map((assetType) => (
									<AssetTypeCard
										key={assetType.uuid}
										assetType={assetType}
										assets={assetsByAssetType[assetType.uuid] ?? EMPTY_ARRAY}
										assetTypeStatuses={assetTypeStatusesByAssetType[assetType.uuid] ?? EMPTY_ARRAY}
										onClick={handleClickAssetTypeCard}
										className={cx(interactiveCardCva(), "h-72 cursor-pointer")}
										size="sm"
									/>
								))
							) : (
								<Text accentColor="base" weight="regular" size="sm">
									No asset types.
								</Text>
							)}
						</div>
					</SegmentedTabs.Content>
				</SegmentedTabs.Root>
			</div>
		</ViewContainer>
	)
})
AssetTypesView.displayName = "AssetTypesView"
